/**
 * Deep Link Utility
 *
 * Generates shareable deep links for story re-entry.
 * Links encode the document ID and can be used for QR codes and email sharing.
 * 
 * Now uses D1 database for persistence with 60-day expiry.
 */

import { loadStorySnapshot, type StorySnapshot } from './storySnapshot';
import { persistDocument, type PersistResponse } from './documentPersistence';
import { isValidDocId, assertValidDocId } from './docId';

const BASE_URL = typeof window !== 'undefined' ? window.location.origin : 'https://atlas-phm.uk';

/**
 * Build a shareable URL for a customer story document.
 * This is the single source of truth for URL generation used by:
 * - QR codes
 * - Text links under QR
 * - Email sharing
 * - Copy link buttons
 * 
 * @param docId - The document ID (must be valid format: doc_<id> (letters, numbers, _ or -))
 * @param baseUrl - Base URL (defaults to current origin or atlas-phm.uk)
 * @returns The complete shareable URL
 * @throws Error if docId is invalid
 */
export function buildInteractiveShareUrl(docId: string, baseUrl: string = BASE_URL): string {
  // Defensive assertion - ensure we never generate invalid URLs
  assertValidDocId(docId);
  
  // Remove trailing slash from baseUrl to avoid double slashes
  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  
  // Route format: /hub/:docId (site-visit main UI)
  return `${cleanBaseUrl}/hub/${docId}`;
}

/**
 * Build a shareable URL for the decision-map portal UI.
 *
 * @param docId - The document ID (must be valid format: doc_<id> (letters, numbers, _ or -))
 * @param baseUrl - Base URL (defaults to current origin or atlas-phm.uk)
 * @returns The complete portal URL
 * @throws Error if docId is invalid
 */
export function buildPortalShareUrl(docId: string, baseUrl: string = BASE_URL): string {
  assertValidDocId(docId);

  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

  return `${cleanBaseUrl}/portal/view/${docId}`;
}

/**
 * Validate that a docId is in the correct format before using it.
 * Use this as a defensive check before rendering QR codes or links.
 * 
 * @param docId - The document ID to validate
 * @returns true if valid, false otherwise (logs warning if invalid)
 */
export function validateDocIdForSharing(docId: string | null | undefined): boolean {
  if (!isValidDocId(docId)) {
    console.warn('Invalid docId for share link', { docId });
    return false;
  }
  return true;
}

/**
 * Generate a shareable deep link for the current story.
 * Persists the snapshot to D1 and returns both the full URL and document ID.
 * 
 * Every call creates a NEW document — never updates existing ones.
 * 
 * @returns Share link object with url and documentId, or null if no snapshot exists
 * @throws Error if snapshot exists but persistence fails
 */
export async function generateDeepLink(
  baseUrl: string = BASE_URL,
  target: 'portal' | 'interactive' = 'portal',
): Promise<{ url: string; documentId: string } | null> {
  const snapshot = loadStorySnapshot();
  if (!snapshot) {
    console.error('[deepLink] Cannot generate link: no story snapshot found');
    return null;
  }

  // Guardrail: Validate snapshot has required fields before attempting persist
  if (!snapshot.version || !snapshot.updatedAt) {
    console.error('[deepLink] CRITICAL: Snapshot missing required fields', {
      hasVersion: !!snapshot.version,
      hasUpdatedAt: !!snapshot.updatedAt,
      keys: Object.keys(snapshot)
    });
    throw new Error('Cannot generate link: snapshot is missing required fields');
  }

  // Guardrail: Validate snapshot structure
  if (typeof snapshot !== 'object' || snapshot === null) {
    console.error('[deepLink] CRITICAL: Snapshot is not a valid object', {
      snapshotType: typeof snapshot,
      snapshot: snapshot
    });
    throw new Error('Cannot generate link: snapshot is not a valid object');
  }

  try {
    // Persist to D1 - this MUST succeed before we generate a share link
    const response: PersistResponse = await persistDocument(snapshot);
    
    // Guardrail: Validate response has required fields
    if (!response || !response.docId) {
      console.error('[deepLink] CRITICAL: Persist succeeded but returned no docId');
      throw new Error('Persist returned invalid response');
    }
    
    // Validate the docId before building URL
    if (!validateDocIdForSharing(response.docId)) {
      console.error('[deepLink] CRITICAL: API returned invalid docId:', response.docId);
      throw new Error('Invalid docId format returned from persist API');
    }
    
    // Build full URL using centralized function
    const fullUrl = target === 'portal'
      ? buildPortalShareUrl(response.docId, baseUrl)
      : buildInteractiveShareUrl(response.docId, baseUrl);

    console.log('[deepLink] Share link generated successfully:', {
      docId: response.docId,
      url: fullUrl,
      target,
    });
    
    return { 
      url: fullUrl, 
      documentId: response.docId 
    };
  } catch (error) {
    console.error('[deepLink] Failed to persist document for deep link:', error);
    // Re-throw to ensure calling code knows this failed
    throw error;
  }
}

/**
 * Get or create a deep link for the current session.
 * 
 * IMPORTANT: Call clearCachedDeepLink() whenever the underlying story
 * data changes (e.g. new recommendation run) so the next call here
 * generates a fresh, unique link instead of reusing a stale one.
 */
let cachedDeepLink: { url: string; documentId: string } | null = null;
let cachedSnapshotTimestamp: string | null = null;

export async function getOrCreateDeepLink(
  baseUrl: string = BASE_URL,
  target: 'portal' | 'interactive' = 'portal',
): Promise<{ url: string; documentId: string } | null> {
  // Auto-invalidate if the underlying snapshot has changed
  const snapshot = loadStorySnapshot();
  const currentTimestamp = snapshot?.updatedAt ?? null;

  if (cachedDeepLink && cachedSnapshotTimestamp === currentTimestamp) {
    return cachedDeepLink;
  }

  // Snapshot changed (or first call) — generate a new unique link
  // Note: generateDeepLink now throws errors instead of returning null on failures
  try {
    cachedDeepLink = await generateDeepLink(baseUrl, target);
    cachedSnapshotTimestamp = currentTimestamp;
    return cachedDeepLink;
  } catch (error) {
    // Clear cache on error to force retry next time
    cachedDeepLink = null;
    cachedSnapshotTimestamp = null;
    // Re-throw so calling code can handle the error appropriately
    throw error;
  }
}

/**
 * Clear the cached deep link.
 * Call this when story data changes so the next getOrCreateDeepLink()
 * generates a fresh, unique link.
 */
export function clearCachedDeepLink(): void {
  cachedDeepLink = null;
  cachedSnapshotTimestamp = null;
}

/**
 * Build the mailto: URL for emailing the report
 */
export function buildEmailMailtoUrl(
  email: string,
  deepLinkUrl: string,
  reportType: 'compact' | 'full' = 'compact'
): string {
  // Validate email format - simple validation for mailto:
  if (!email || !email.trim()) {
    throw new Error('Email address is required');
  }
  
  // Basic email validation - must have @ with content on both sides
  if (!email.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/)) {
    throw new Error('Email address must be in valid format (e.g., user@example.com)');
  }

  const subject = encodeURIComponent('Your heating options summary – Atlas PHM');

  // Construct body text with proper line breaks
  const bodyText = `Here's a copy of your personalised heating options summary.

• View your interactive summary here:
${deepLinkUrl}

This report is indicative guidance based on the information provided.
Final recommendations follow an in-home survey.

— Atlas PHM`;

  // Encode body with proper escaping
  const body = encodeURIComponent(bodyText);

  return `mailto:${encodeURIComponent(email)}?subject=${subject}&body=${body}`;
}

/**
 * Email copy constants (for use when building backend email service)
 */
export const EMAIL_COPY = {
  subject: 'Your heating options summary – Atlas PHM',
  bodyTemplate: `Here's a copy of your personalised heating options summary.

• Your report is attached as a PDF
• You can explore the details again using the link below

View your interactive summary:
{deep_link}

This report is indicative guidance based on the information provided.
Final recommendations follow an in-home survey.

— Atlas PHM`,
};
